import { useState, useEffect } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';
import { FindingEvidence, FindingEvidenceResponse } from '../types/finding';

interface FindingEvidenceState {
  evidence: FindingEvidence | null;
  loading: boolean;
  error: string | null;
}

/** The code a finding points at; `null` when there is none or the load fails. */
export const useFindingEvidence = (findingId: string | undefined) => {
  const api = useAuthenticatedApi();
  const [state, setState] = useState<FindingEvidenceState>({
    evidence: null,
    loading: Boolean(findingId),
    error: null,
  });

  useEffect(() => {
    if (!api || !findingId) {
      setState({ evidence: null, loading: false, error: null });
      return;
    }

    let cancelled = false;
    setState({ evidence: null, loading: true, error: null });

    const loadEvidence = async () => {
      try {
        const response = await api.get<FindingEvidenceResponse>(apiEndpoints.findings.evidence(findingId));
        if (!cancelled) {
          setState({ evidence: response?.evidence ?? null, loading: false, error: null });
        }
      } catch (error) {
        console.error('Failed to load finding evidence:', error);
        if (!cancelled) {
          setState({ evidence: null, loading: false, error: 'Failed to load the code for this finding' });
        }
      }
    };

    loadEvidence();
    return () => {
      cancelled = true;
    };
  }, [api, findingId]);

  return state;
};
